import { useCallback } from 'react';

export type MaskType = 'cardNumber' | 'expiry' | 'cvv' | 'phone' | 'custom';

interface UseInputMaskOptions {
  type: MaskType;
  customMask?: string;
  customReplacements?: Record<string, RegExp>;
}

export const useInputMask = ({
  type,
  customMask,
  customReplacements,
}: UseInputMaskOptions) => {
  const formatValue = useCallback(
    (value: string): string => {
      switch (type) {
        case 'cardNumber':
          return formatCardNumber(value);
        case 'expiry':
          return formatExpiry(value);
        case 'cvv':
          return formatCVV(value);
        case 'phone':
          return formatPhone(value);
        case 'custom':
          return formatCustom(value, customMask!, customReplacements);
        default:
          return value;
      }
    },
    [type, customMask, customReplacements]
  );

  const validateInput = useCallback((key: string, field: string): boolean => {
    switch (field) {
      case 'cardNumber':
      case 'cvv':
        return (
          /\d/.test(key) ||
          ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(
            key
          )
        );
      case 'expiry':
        return (
          /\d/.test(key) ||
          [
            'Backspace',
            'Delete',
            'Tab',
            'ArrowLeft',
            'ArrowRight',
            '/',
          ].includes(key)
        );
      case 'phone':
        return (
          /\d/.test(key) ||
          [
            'Backspace',
            'Delete',
            'Tab',
            'ArrowLeft',
            'ArrowRight',
            '+',
            '-',
            '(',
            ')',
            ' ',
          ].includes(key)
        );
      default:
        return true;
    }
  }, []);

  return {
    formatValue,
    validateInput,
  };
};

// Функции форматирования
const formatCardNumber = (value: string): string => {
  const cleaned = value.replace(/\s/g, '').replace(/\D/g, '');
  const groups = cleaned.match(/.{1,4}/g);
  return groups ? groups.join(' ') : cleaned;
};

const formatExpiry = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length >= 2) {
    const month = cleaned.substring(0, 2);
    const year = cleaned.substring(2, 4);
    return `${month}/${year}`;
  }
  return cleaned;
};

const formatCVV = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  return cleaned.substring(0, 3); // Строго 3 цифры
};

const formatPhone = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length === 0) return '';
  if (cleaned.length <= 3) return `+${cleaned}`;
  if (cleaned.length <= 6) return `+${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
  if (cleaned.length <= 8)
    return `+${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
  return `+${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8, 10)}`;
};

const formatCustom = (
  value: string,
  mask: string,
  replacements?: Record<string, RegExp>
): string => {
  const defaultReplacements: Record<string, RegExp> = {
    '0': /\d/,
    A: /[A-Za-z]/,
    S: /[A-Za-z\s]/,
    '#': /[0-9A-Za-z]/,
  };

  const finalReplacements = { ...defaultReplacements, ...replacements };
  let result = '';
  let valueIndex = 0;

  for (let i = 0; i < mask.length && valueIndex < value.length; i++) {
    const maskChar = mask[i];
    const valueChar = value[valueIndex];

    if (finalReplacements[maskChar]?.test(valueChar)) {
      result += valueChar;
      valueIndex++;
    } else if (maskChar === valueChar) {
      result += maskChar;
      valueIndex++;
    } else {
      result += maskChar;
    }
  }

  return result;
};
